import {
  type BulkArchivePlansResponse,
  bulkArchivePlansResponseDto,
  type CreatePlanBody,
  type PlanDto,
  planDto,
  type TargetedPlanDto,
  targetedPlanDto,
  type UpdatePlanBody,
} from '@planuze/admin-contracts';
import {
  ApiError,
  buildQuery,
  cursorPageSchema,
  parseJson,
  readErrorCode,
  request,
} from './http.js';

/* Cliente REST de planos (ADR-0419/0435). Públicos = CRUD (cria/edita/arquiva via
   billing → Stripe); direcionados (negociados) = list (criados via createPlan com alvo).
   As duas listas são paginadas por cursor keyset (`{ items, nextCursor }`, ADR-0713). */

const HTTP_NOT_FOUND = 404;
const HTTP_BAD_REQUEST = 400;
const HTTP_CONFLICT = 409;

/* Erros do CRUD do catálogo de planos (ADR-0414/0419) — preserva o código real do
   billing (`conflict`/`invalid`) p/ `planCatalogErrorMessage` (não reusa o mapper
   de descontos, que mapeia todo 400 p/ a mensagem de desconto). */
async function planCatalogError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  switch (res.status) {
    case HTTP_NOT_FOUND:
      return new ApiError(res.status, 'not_found', code || 'plan not found');
    case HTTP_CONFLICT:
      return new ApiError(
        res.status,
        code === 'plan_key_taken' ? 'plan_key_taken' : 'conflict',
        code || 'conflict',
        code,
      );
    case HTTP_BAD_REQUEST:
      return new ApiError(res.status, 'invalid', code || 'invalid plan', code);
    default:
      return new ApiError(res.status, 'server_error', code || 'request failed');
  }
}

const plansPageSchema = cursorPageSchema(planDto);
const targetedPlansPageSchema = cursorPageSchema(targetedPlanDto);

/** Query de paginação keyset das listas (cursor opaco `(createdAt,id)`). Interna — os call
    sites passam literais (`{ cursor }`); knip evita export sem consumidor por nome. */
interface PlansPageQuery {
  readonly cursor?: string | undefined;
}

/* Página de planos públicos. `includeArchived=true` SEMPRE (ADR-0713): o filtro Ativo/Arquivado
   é client-side sobre `status`, então a lista carrega os arquivados; a paginação é por cursor. */
export async function listPlans(
  query: PlansPageQuery = {},
): Promise<{ readonly items: readonly PlanDto[]; readonly nextCursor: string | null }> {
  const res = await request(
    `/v1/admin/plans${buildQuery({ includeArchived: 'true', cursor: query.cursor })}`,
  );
  if (!res.ok) throw await planCatalogError(res);
  const page = await parseJson(res, plansPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

export async function createPlan(body: CreatePlanBody): Promise<void> {
  const res = await request('/v1/admin/plans', { method: 'POST', body });
  if (!res.ok) throw await planCatalogError(res);
}

export async function updatePlan(id: string, body: UpdatePlanBody): Promise<void> {
  const res = await request(`/v1/admin/plans/${encodeURIComponent(id)}`, { method: 'PATCH', body });
  if (!res.ok) throw await planCatalogError(res);
}

export async function archivePlan(id: string): Promise<void> {
  const res = await request(`/v1/admin/plans/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
  });
  if (!res.ok) throw await planCatalogError(res);
}

export async function bulkArchivePlans(ids: readonly string[]): Promise<BulkArchivePlansResponse> {
  const res = await request('/v1/admin/plans/bulk', {
    method: 'POST',
    body: { ids, action: 'archive' },
  });
  if (!res.ok) throw await planCatalogError(res);
  return parseJson(res, bulkArchivePlansResponseDto);
}

/* Planos DIRECIONADOS (negociados) — página keyset (ADR-0435). Criar/arquivar reusa
   `createPlan`/`archivePlan` (mesmo plano completo, com alvo). */
export async function listTargetedPlans(
  query: PlansPageQuery = {},
): Promise<{ readonly items: readonly TargetedPlanDto[]; readonly nextCursor: string | null }> {
  const res = await request(`/v1/admin/targeted-plans${buildQuery({ cursor: query.cursor })}`);
  if (!res.ok) throw await planCatalogError(res);
  const page = await parseJson(res, targetedPlansPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}
